<script lang="ts">
	import { Button } from '$lib/components/ui/button';
	import * as m from '$lib/paraglide/messages';
	import { ListMinus, X } from '@lucide/svelte';
	import { page } from '$app/state';
	import NotificationBar from '$lib/components/NotificationBar.svelte';

	let isScrolled = $state(false);
	let isMobileMenuOpen = $state(false);

	const navLinks = [
		{ label: m.nav_home(), href: '/' },
		{ label: m.nav_solutions(), href: '/solutions' },
		{ label: m.nav_case_studies(), href: '/case-studies' },
		{ label: m.academy(), href: '/academy' },
		{ label: m.resources(), href: '/resources' },
		{ label: m.nav_about(), href: '/about-me' }
	];

	function isActive(href: string): boolean {
		if (href === '/') return page.url.pathname === '/';
		return page.url.pathname.startsWith(href);
	}

	function toggleMobileMenu() {
		isMobileMenuOpen = !isMobileMenuOpen;
	}

	$effect(() => {
		if (typeof window !== 'undefined') {
			window.addEventListener(
				'scroll',
				() => {
					isScrolled = window.scrollY > 50;
				},
				{ passive: true }
			);
		}
	});

	$effect(() => {
		if (typeof document !== 'undefined') {
			document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
		}
	});
</script>

<div class="fixed top-0 right-0 left-0 z-50">
	<NotificationBar
		text={page.data.notifBar?.text ?? ''}
		link={page.data.notifBar?.link ?? ''}
		cta={page.data.notifBar?.cta ?? ''}
		visible={page.data.notifBar?.visible ?? false}
		closeBtn={page.data.notifBar?.closeBtn ?? false}
	/>
	<header
		class="fixed right-0 left-0 z-50 h-20 transition-[background,box-shadow,transform] duration-300
         {isScrolled ? 'border-b border-border bg-background shadow-lg backdrop-blur-md' : ''}"
	>
		<nav
			class="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16"
			aria-label="Main navigation"
		>
			<div class="flex h-14 items-center justify-between sm:h-16 lg:h-20">
				<!-- Logo -->
				<a href="/" class="group flex items-center gap-2" aria-label="Accueil">
					<div
						class="flex h-8 w-8 items-center justify-center rounded-lg
                 bg-gradient-to-br from-juslin-blue to-juslin-gold sm:h-10 sm:w-10"
					>
						<span class="text-sm font-black text-juslin-black">JK</span>
					</div>
					<span class="font-heading text-lg font-bold text-foreground">
						Juslin <span class="text-blue-400">Komenan</span>
					</span>
				</a>

				<!-- Desktop nav -->
				<div class="hidden items-center gap-6 xl:flex">
					{#each navLinks as link (link.href)}
						<a
							href={link.href}
							class="font-heading text-sm font-bold transition-colors
                   hover:text-blue-400
                   {isActive(link.href) ? 'text-blue-400' : 'text-foreground'}"
						>
							{link.label}
						</a>
					{/each}

					<Button href="/#audit" class="cursor-pointer rounded-full bg-juslin-blue font-bold">
						{m.start_my_project()}
						<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 7l5 5m0 0l-5 5m5-5H6"
							/>
						</svg>
					</Button>
				</div>

				<!-- Mobile burger -->
				<button
					onclick={toggleMobileMenu}
					class="relative z-[70] flex h-10 w-10 cursor-pointer items-center
               justify-center rounded-md transition-colors xl:hidden"
					aria-expanded={isMobileMenuOpen}
					aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
				>
					{#if isMobileMenuOpen}
						<X size={28} strokeWidth={2.5} />
					{:else}
						<ListMinus size={28} strokeWidth={2.5} />
					{/if}
				</button>
			</div>
		</nav>

		<!-- Mobile menu overlay -->
		<div
			class="fixed inset-0 z-[60] transition-all duration-300 xl:hidden
           {isMobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'}"
		>
			<!-- Backdrop -->
			<button
				onclick={() => (isMobileMenuOpen = false)}
				class="absolute inset-0 bg-juslin-black/80 backdrop-blur-sm"
				aria-label="Fermer le menu"
			></button>

			<!-- Drawer -->
			<nav
				class="absolute top-0 right-0 flex h-full w-full max-w-[300px] flex-col
             border-l border-border bg-juslin-dark-section
             transition-transform duration-500
             {isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}"
				aria-label="Navigation mobile"
			>
				<!-- Links -->
				<div class="mt-16 flex flex-1 flex-col gap-1 p-4">
					{#each navLinks as link (link.href)}
						<a
							href={link.href}
							onclick={() => (isMobileMenuOpen = false)}
							class="rounded-lg border-b border-border/50 px-3 py-3 text-lg font-bold
                   transition-colors hover:text-blue-400
                   {isActive(link.href) ? 'text-blue-400' : 'text-foreground'}"
						>
							{link.label}
						</a>
					{/each}
				</div>

				<!-- CTA -->
				<div class="border-t border-border p-4">
					<Button
						href="/#audit"
						onclick={() => (isMobileMenuOpen = false)}
						class="w-full cursor-pointer rounded-full bg-juslin-blue py-4 font-bold"
					>
						{m.start_my_project()}
						<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 7l5 5m0 0l-5 5m5-5H6"
							/>
						</svg>
					</Button>
				</div>
			</nav>
		</div>
	</header>
</div>
